import { Candidate } from "../../../TalentsStatusAPI";

const sortByName = (a: Candidate, b: Candidate) =>
  `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);

const sortByLatestActivity = (a: Candidate, b: Candidate) =>
  (b.llt ?? Number.MIN_SAFE_INTEGER) - (a.llt ?? Number.MIN_SAFE_INTEGER) ||
  sortByName(a, b);

/**
 * Sorts recommended candidates into matching tiers and by latest activity inside each tier.
 *
 * Candidates over 50 km away are placed in tier 4, ordered by qualification tier within that group.
 * Tier 1: workTitle + language + training + certificate + skill
 * Tier 2: workTitle + language + training + certificate
 * Tier 3: remaining candidates with missing or incomplete requirements
 * Tier 4: distance over 50 km
 */
export const getRecommendedCandidateTier = (candidate: Candidate): number => {
  if (
    candidate.distanceToWorkLocation != null &&
    candidate.distanceToWorkLocation > 50_000
  )
    return 4;

  return getQualificationTier(candidate);
};

const getQualificationTier = (candidate: Candidate): number => {
  const hasTierOneMatches = (candidate: Candidate) =>
    candidate.matchingDetails?.workTitle?.match &&
    candidate.matchingDetails?.language?.match &&
    candidate.matchingDetails?.training?.match &&
    candidate.matchingDetails?.certificate?.match &&
    candidate.matchingDetails?.skill?.match;

  const hasTierTwoMatches = (candidate: Candidate) =>
    candidate.matchingDetails?.workTitle?.match &&
    candidate.matchingDetails?.language?.match &&
    candidate.matchingDetails?.training?.match &&
    candidate.matchingDetails?.certificate?.match;

  if (hasTierOneMatches(candidate)) return 1;
  if (hasTierTwoMatches(candidate)) return 2;
  return 3;
};

const sortRecommendedCandidates = (
  candidates: Candidate[],
  withinTierComparator: (a: Candidate, b: Candidate) => number,
): Candidate[] => {
  return [...candidates].sort(
    (a, b) =>
      getRecommendedCandidateTier(a) - getRecommendedCandidateTier(b) ||
      getQualificationTier(a) - getQualificationTier(b) ||
      withinTierComparator(a, b),
  );
};

export const sortRecommendedNewCandidates = (candidates: Candidate[]) =>
  sortRecommendedCandidates(candidates, sortByName);

export const sortRecommendedActiveCandidates = (candidates: Candidate[]) =>
  sortRecommendedCandidates(candidates, sortByLatestActivity);
